/**
 * useProfileView — the "which hat am I wearing" selector.
 *
 * Many Kantera users hold several roles at once in the same org: a parent who
 * also coaches, an owner who is also a coach and a parent. The backend
 * authorizes by the full set of roles in the JWT, so these users genuinely have
 * all that access. This hook does NOT change access — it only picks which view
 * the sidebar presents, so a person can focus on one hat at a time instead of
 * seeing every nav item for every role at once.
 *
 * Purely presentational: the active view is a client preference (localStorage,
 * scoped per user). It composes with the org switcher — switching orgs changes
 * `userRoles`, the available views recompute; switching view changes the hat.
 */

import { useCallback, useMemo, useState } from 'react';
import type { Role } from '@kantera/types';
import { defaultViewFor, type ProfileViewId } from '../utils/userRoles';

export type { ProfileViewId };

export interface ProfileView {
  id: ProfileViewId;
  /** i18n key under webProfileSwitcher. */
  labelKey:
    | 'viewPlatform'
    | 'viewStaff'
    | 'viewCoach'
    | 'viewParent'
    | 'viewReferee'
    | 'viewPlayer';
  /** Roles that grant this view; the view is offered if the user has any. */
  roles: Role[];
}

/**
 * Every member of the Role union must appear in exactly one view, or a user
 * holding only that role would have no view and lose their whole sidebar.
 * Covered: founder, platform_admin, owner, admin, coach, assistant_coach,
 * team_manager, parent, referee, player.
 */
const VIEW_DEFS: ProfileView[] = [
  { id: 'platform', labelKey: 'viewPlatform', roles: ['founder', 'platform_admin'] },
  // 'coach' used to live in this list. Bundling it here meant an owner who also
  // coaches — the normal case at a small club, and Taylor's case at AFC Union —
  // had no way to say "right now I am being a coach", because the two collapsed
  // into one hat. It also made the label lie: this view is club STAFF, but it
  // was labelled "Coach", so an owner sat on the club-admin console under a
  // badge reading Coach. That is what "coach sees admin dashboard" looked like.
  { id: 'staff', labelKey: 'viewStaff', roles: ['owner', 'admin', 'team_manager'] },
  // assistant_coach (KAN-115) wears the coach hat. Their roles already include the 'coach' they act
  // as (actingRoles); listing the held role here too keeps it in the view's filtered roles, so the
  // sidebar still labels them by the role they hold rather than "Coach".
  { id: 'coach', labelKey: 'viewCoach', roles: ['coach', 'assistant_coach'] },
  { id: 'parent', labelKey: 'viewParent', roles: ['parent'] },
  { id: 'referee', labelKey: 'viewReferee', roles: ['referee'] },
  { id: 'player', labelKey: 'viewPlayer', roles: ['player'] },
];

/**
 * Default view preference when nothing is stored: land on the hat most people
 * spend their time in (staff), not the highest-privilege one. A user who only
 * holds a platform or parent role still gets that view because the default
 * falls through to the first available.
 */
const DEFAULT_PREFERENCE: ProfileViewId[] = [
  'staff',
  'coach',
  'platform',
  'parent',
  'referee',
  'player',
];

/**
 * The last-resort view when the user has NO roles at all.
 *
 * Found by name, not by index. This was `VIEW_DEFS[1]!` with a comment saying
 * "'staff' only if roles list is empty" — true only while staff happened to sit
 * at index 1. Inserting the coach hat above it would have silently changed the
 * zero-roles fallback to a different view, with nothing to catch it.
 */
const FALLBACK_VIEW: ProfileView = VIEW_DEFS.find((v) => v.id === 'staff')!;

const STORAGE_PREFIX = 'kantera_active_view';

function storageKey(userId: string | undefined): string {
  // Scoped per user so a shared browser doesn't carry one person's view choice
  // into the next person's session.
  return userId ? `${STORAGE_PREFIX}:${userId}` : STORAGE_PREFIX;
}

function readStored(userId: string | undefined): ProfileViewId | null {
  try {
    const v = window.localStorage.getItem(storageKey(userId));
    return v && VIEW_DEFS.some((d) => d.id === v) ? (v as ProfileViewId) : null;
  } catch {
    return null;
  }
}

export interface UseProfileViewResult {
  activeView: ProfileView;
  availableViews: ProfileView[];
  setView: (id: ProfileViewId) => void;
  /** The roles the sidebar should filter nav by while this view is active. */
  effectiveRoles: Role[];
}

export function useProfileView(
  userRoles: readonly Role[],
  userId?: string,
): UseProfileViewResult {
  const availableViews = useMemo(
    () => VIEW_DEFS.filter((v) => v.roles.some((r) => userRoles.includes(r))),
    [userRoles],
  );

  const [activeId, setActiveId] = useState<ProfileViewId | null>(() => readStored(userId));

  // Resolve the active view against what's actually available. If the stored
  // preference isn't available in this org (e.g. after an org switch), fall
  // back to the default preference order, then to the first available view.
  const activeView = useMemo(() => {
    const stored = availableViews.find((v) => v.id === activeId);
    if (stored) return stored;
    // No stored choice: land where the old role-ranking would have put them, so
    // a first-ever load is unchanged by the introduction of hats.
    const natural = availableViews.find((v) => v.id === defaultViewFor(userRoles));
    if (natural) return natural;
    for (const pref of DEFAULT_PREFERENCE) {
      const hit = availableViews.find((v) => v.id === pref);
      if (hit) return hit;
    }
    return availableViews[0] ?? FALLBACK_VIEW;
  }, [availableViews, activeId, userRoles]);

  const setView = useCallback(
    (id: ProfileViewId) => {
      setActiveId(id);
      try {
        window.localStorage.setItem(storageKey(userId), id);
      } catch {
        /* private mode — the choice still holds for this session */
      }
    },
    [userId],
  );

  const effectiveRoles = useMemo(() => {
    const filtered = userRoles.filter((r) => activeView.roles.includes(r));
    // Safety net: a role that belongs to no view (should not happen now that
    // VIEW_DEFS covers the whole union, but guards future role additions) would
    // otherwise strip the user's entire nav. Never return empty when the user
    // actually has roles.
    return filtered.length > 0 ? filtered : [...userRoles];
  }, [userRoles, activeView]);

  return { activeView, availableViews, setView, effectiveRoles };
}
